/**
 * Color utility functions for working with hex colors
 * Generates color variations for consistent theming
 */

/**
 * Convert hex to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Lighten a color by a percentage
 */
function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  const r = Math.round(rgb.r + (255 - rgb.r) * factor);
  const g = Math.round(rgb.g + (255 - rgb.g) * factor);
  const b = Math.round(rgb.b + (255 - rgb.b) * factor);

  return rgbToHex(
    Math.min(255, r),
    Math.min(255, g),
    Math.min(255, b)
  );
}

/**
 * Darken a color by a percentage
 */
function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = 1 - (percent / 100);
  const r = Math.round(rgb.r * factor);
  const g = Math.round(rgb.g * factor);
  const b = Math.round(rgb.b * factor);

  return rgbToHex(
    Math.max(0, r),
    Math.max(0, g),
    Math.max(0, b)
  );
}

/**
 * Generate color variations from a base color
 * Returns colors similar to Tailwind's shade system
 */
export function getColorVariants(baseColor) {
  if (!baseColor) {
    // Default to amber if no color provided
    baseColor = '#F59E0B';
  }

  return {
    50: lightenColor(baseColor, 95),   // Lightest
    100: lightenColor(baseColor, 90),  // Very light (badge backgrounds)
    200: lightenColor(baseColor, 75),  // Light
    300: lightenColor(baseColor, 60),  // Light-medium
    400: lightenColor(baseColor, 40),  // Medium-light
    500: baseColor,                    // Base color (user selected)
    600: darkenColor(baseColor, 10),   // Medium-dark (borders)
    700: darkenColor(baseColor, 25),   // Dark
    800: darkenColor(baseColor, 40),   // Very dark
    900: darkenColor(baseColor, 55),   // Darkest
  };
}

/**
 * Get a specific shade of a color
 */
export function getColorShade(baseColor, shade = 500) {
  const variants = getColorVariants(baseColor);
  return variants[shade] || baseColor;
}

/**
 * Get colors for UI elements based on a base color
 */
export function getUIColors(baseColor) {
  const variants = getColorVariants(baseColor);

  return {
    // Background colors (light)
    bgLight: variants[50],
    bgMedium: variants[100],
    bgHover: variants[200],

    // Border colors
    borderLight: variants[200],
    borderMedium: variants[400],
    borderStrong: variants[600],

    // Text colors
    textLight: variants[600],
    textMedium: variants[700],
    textStrong: variants[900],

    // The original color
    primary: baseColor,

    // Common UI patterns
    badge: {
      background: variants[100],
      border: variants[300],
      text: variants[700]
    },
    card: {
      borderAccent: variants[500], // Use the actual user-selected color for borders
      hoverBorder: variants[400]
    }
  };
}

export default {
  hexToRgb,
  rgbToHex,
  lightenColor,
  darkenColor,
  getColorVariants,
  getColorShade,
  getUIColors
};